import { Formik, useFormikContext, Form } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import { validateFiscalNumber } from "utils/utils";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { DRF_DATE_FORMAT } from "constants";
import { useCommunitiesAPI } from "service/CommunitiesAPIService";
import { UserFeedbackContext } from "contexts/UserFeedbackContext";
import { Alert, Box, Divider, FormHelperText, Grid2, Skeleton, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { IndieSimpleTable } from "page-sections/common/IndieSimpleTable";
import IndieBackButton from "components/forms/IndieBackButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieTextField from "components/forms/IndieTextField";
import SelectYesNo from "components/SelectYesNo";
import CommunityTypeSelector, { communityTypes } from "components/forms/communities/CommunityTypeSelector";
import { communitySharingTypes, IndieDSOIDField, IndieLabelAndValue } from "./NewCommunityDialog";
import ValueListWithSelectCard from "components/forms/ValueListWithSelectCard";
import { BillingPeriodSelect } from "components/BillingPeriodSelect";
import IndiePaymentTermsSelect from "components/forms/tariffs/IndiePaymentTermsSelect";
import IndieReceiptTermsSelect from "components/forms/tariffs/IndieReceiptTermsSelect";
import IndieSaveButton from "components/forms/IndieSaveButton";
import { useTranslation } from "react-i18next";
import CommunityBannerUpload from "./CommunityBannerUpload";
import { CommunityContext } from "contexts/communities/CommunityContext";
import CommunityStatusSelect, { CommunityStatusList } from "components/forms/communities/CommunityStatusSelect";
import { ClientsContext } from "contexts/ClientsContext";
import IndieDesktopDatePicker from "components/forms/IndieDesktopDatePickerV2";
import IndieCheckbox from "components/forms/IndieCheckbox";

export const CommunitiesCommunityForm = ({ handleSuccess }) => {
    const { setAndShowErrorMessage, setAndShowSuccessMessage } = useContext(UserFeedbackContext);
    const communitiesAPI = useCommunitiesAPI();
    //FIXME: Utilizar outro contexto que não o clients context
    const { fetchCommunityManagers } = useContext(ClientsContext);
    const { getCommunitiesTariffModels } = useContext(CommunityContext);

    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [ownerEntities, setOwnerEntities] = useState([]);
    const [loadingOwnerEntities, setLoadingOwnerEntities] = useState(true);
    const [selectedOwnerEntity, setSelectedOwnerEntity] = useState(null);
    const [tariffModels, setTariffModels] = useState([]);

    const columns = useMemo(() => [
        { field: "entity.name", label: t("label.table.header.companyName"), align: "left" },
        { field: "entity.fiscal_number", label: t("label.table.header.fiscalNumber"), align: "left" },
        { field: "resp_party.name", label: t("label.table.header.respName"), align: "left" },
    ], []);

    const initialValues = {
        name: "",
        dso_id: "",
        activation_date: null,
        agent_entity: null,
        type: null,
        sharing_type: null,
        status: null,
        banner_image: null,
        is_egac_dgeg: false,
    };

    const step1ValidationSchema = Yup.object().shape({
        agent_entity: Yup.object().required(t("error.forms.mandatory")),
        managing_entity: Yup.object().required(t("error.forms.mandatory")),
    });

    const step2ValidationSchema = Yup.object().shape({
        is_egac_dgeg: Yup.bool().notRequired(),
        dgeg_fiscal_number: Yup.string().notRequired()
            .matches(/^\d{9}$/, t('error.forms.fiscalNumberDigits'), { excludeEmptyString: true })
            .test('valid-nif', t('error.forms.fiscalNumberInvalid'), (value) => {
                if (!value) return true;
                return validateFiscalNumber(value);
            }),
        dgeg_name: Yup.string().notRequired(),
    });

    const step3ValidationSchema = Yup.object().shape({
        type: Yup.string().required(t("error.forms.mandatory")),
    });

    const step4ValidationSchema = Yup.object().shape({
        sharing_type: Yup.string().required(t("error.forms.mandatory")),
    });

    const step5ValidationSchema = Yup.object().shape({
        status: Yup.string().required(t("error.forms.mandatory"))
    });

    const step6ValidationSchema = Yup.object().shape({
        name: Yup.string().notRequired(),
        dso_id: Yup.number().required(t("error.forms.mandatory")),
        activation_date: Yup.date().when(["status"], {
            is: (status) => status === "active" || status === "suspended",
            then: (schema) => schema.required(t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const step7ValidationSchema = Yup.object().shape({
        invoice_network_costs: Yup.string().required(t("error.forms.mandatory")),
        billing_period: Yup.string().required(t("error.forms.mandatory")),
        sale_payment_terms: Yup.string().required(t("error.forms.mandatory")),
        purchase_payment_terms: Yup.string().required(t("error.forms.mandatory")),
    });

    const validationSchemas = [
        step1ValidationSchema,
        step2ValidationSchema,
        step3ValidationSchema,
        step4ValidationSchema,
        step5ValidationSchema,
        step6ValidationSchema,
        step7ValidationSchema
    ];

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const handleSubmit = (values) => {
        if (activeStep < 8) {
            handleNext();
        } else {
            const _values = _.cloneDeep(values);

            const rawDate = _values['activation_date'];
            _values['activation_date'] = rawDate ? moment(rawDate).format(DRF_DATE_FORMAT) : null;
            _values.agent_entity = _values.agent_entity.entity.id;
            _values.managing_entity = _values.managing_entity.entity.id;
            _values.business_type = "communities";

            delete _values.billing_period_object;
            delete _values.invoice_network_costs_object;
            delete _values.purchase_payment_terms_object;
            delete _values.sale_payment_terms_object;
            delete _values.is_egac_dgeg;

            setSubmitting(true);
            communitiesAPI.save(_values, (response) => {
                setAndShowSuccessMessage(t("success.api.create"));
                setSubmitting(false);
                setActiveStep(0);
                if (handleSuccess) {
                    handleSuccess(response.data);
                }
            }, (error) => {
                setSubmitting(false);
                setAndShowErrorMessage(error);
            });
        }
    };

    const fetchOwnerEntities = () => {
        setLoadingOwnerEntities(true);
        fetchCommunityManagers([], 100, 1, false, (response) => {
            setOwnerEntities(response.data.results);
            setLoadingOwnerEntities(false);
        }, (error) => {
            console.error(error);
            setLoadingOwnerEntities(false);
        });
    }

    const fetchTariffModels = () => {
        getCommunitiesTariffModels().then((response) => {
            setTariffModels(response.data);
        }).catch((error) => {
            console.error(error);
            setTariffModels([]);
        });
    }

    const handleOwnerRowClick = (row, formik) => {
        setSelectedOwnerEntity(row);
        formik.setFieldValue("agent_entity", row);
        formik.setFieldValue("managing_entity", row);
    };

    const TariffModelsWatcher = ({ tariffModels }) => {
        const formik = useFormikContext()

        useEffect(() => {
            if (tariffModels && tariffModels.length > 0 && !formik.values.tariff_model) {
                const first = tariffModels[0];
                formik.setFieldValue("tariff_model", first.id);
            }
        }, [tariffModels]);

        return null
    }

    const Step2FieldsWatcher = () => {
        const { values, setFieldValue } = useFormikContext();

        useEffect(() => {
            if (values.is_egac_dgeg && values.agent_entity) {
                setFieldValue("dgeg_fiscal_number", values.agent_entity.entity.fiscal_number || "");
                setFieldValue("dgeg_name", values.agent_entity.entity.name || "");
            }
        }, [values.is_egac_dgeg, values.agent_entity, setFieldValue]);

        return (
            <>
                <IndieCheckbox
                    id={`is_egac_dgeg`}
                    name={`is_egac_dgeg`}
                    label={t("newAgentCommunity.isEGACInDGEG")}
                />
                <IndieTextField id="dgeg_name" name="dgeg_name" label={t("label.form.name")} sx={{ width: 200 }} disabled={values.is_egac_dgeg} />
                <IndieTextField id="dgeg_fiscal_number" name="dgeg_fiscal_number" label={t("label.form.NIF")} sx={{ width: 200 }} disabled={values.is_egac_dgeg} />
            </>
        );
    }

    useEffect(() => {
        fetchOwnerEntities();
        fetchTariffModels();

        return () => {
            setOwnerEntities([]);
        }
    }, []);

    useEffect(() => {
        setActiveStep(0);
    }, []);

    if (!tariffModels || !ownerEntities) {
        return (
            <Skeleton variant="rectangular" width="100%" height={400} />
        )
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[activeStep]}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            onSubmit={handleSubmit}
        >
            {(formik) => (
                <Form>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        <Step key={0}>
                            <StepLabel>{t("label.menu.energyAgent")}</StepLabel>
                            <StepContent>
                                <Box maxWidth={"md"}>
                                    <Stack spacing={2}>
                                        {ownerEntities && ownerEntities.length > 0 && (
                                            <IndieSimpleTable data={ownerEntities} columns={columns} loading={loadingOwnerEntities} onRowClick={(row) => {
                                                handleOwnerRowClick(row, formik);
                                            }} selected={selectedOwnerEntity} variant="outlined" />
                                        )}
                                        {(!ownerEntities || ownerEntities.length === 0) && (
                                            <Alert width={"md"} severity="warning" variant="filled">{t("error.forms.newCommunity.noEGACEntities")}</Alert>
                                        )}
                                        {formik.errors.agent_entity && <FormHelperText id="agent_entity_helper_text" error>{formik.errors.agent_entity}</FormHelperText>}
                                        <Stack direction="row" spacing={2}>
                                            <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                        </Stack>
                                    </Stack>
                                </Box>
                            </StepContent>
                        </Step>
                        <Step key={1}>
                            <StepLabel>{t("newAgentCommunity.egacRegisteredDGEG")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("newAgentCommunity.egacRegisteredDGEG.subtitle")}</Typography>
                                    <Step2FieldsWatcher />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={2}>
                            <StepLabel>{t("label.step.newCommunity.communityTypeStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newAgentCommunity.communityTypeStep.subtitle")}</Typography>
                                    <CommunityTypeSelector name="type" />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={3}>
                            <StepLabel>{t("label.step.newCommunity.communitySharingTypeStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newCommunity.communitySharingTypeStep.subtitle")}</Typography>
                                    <Grid2 container spacing={2}>
                                        {communitySharingTypes.map((type, index) => (
                                            <Grid2 item size={{ xs: 12, md: 6, lg: 3 }} key={index}>
                                                <ValueListWithSelectCard id={`card_sharing_type_${type.code}`} name="sharing_type" code={type.code} title={type.name} description={type.description} disabled={type.disabled} />
                                            </Grid2>
                                        ))}
                                        <Grid2 item size={{ xs: 12 }}>
                                            {formik.errors.sharing_type && <FormHelperText id="sharing_type_helper_text" error>{formik.errors.sharing_type}</FormHelperText>}
                                        </Grid2>
                                    </Grid2>
                                    <TariffModelsWatcher tariffModels={tariffModels} />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={4}>
                            <StepLabel>{t("label.step.newCommunity.communityStatusStep")}</StepLabel>
                            <StepContent>
                                <Box maxWidth={"lg"}>
                                    <Stack spacing={2}>
                                        <Typography variant="subtitle2">{t("label.step.newCommunity.communityStatusStep.subtitle")}</Typography>
                                        <CommunityStatusSelect name="status" />
                                        <Stack direction="row" spacing={2}>
                                            <IndieBackButton variant="outlined" onClick={handleBack} />
                                            <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                        </Stack>
                                    </Stack>
                                </Box>
                            </StepContent>
                        </Step>
                        <Step key={5}>
                            <StepLabel>{t("label.step.newCommunity.communityDataStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newCommunity.communityDataStep.subtitle")}</Typography>
                                    <IndieTextField id="name" name="name" label={t("label.form.name")} sx={{ width: 200 }} />

                                    {formik.values.status === "active" || formik.values.status === "suspended" ? (
                                        <IndieDesktopDatePicker id="activation_date" name="activation_date" label={t("label.form.activationDate")} sx={{ width: 200 }} />
                                    ) : null}

                                    <IndieDSOIDField id="dso_id" name="dso_id" label={t("label.form.code")} type={formik.values?.type} />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={6}>
                            <StepLabel>{t("label.step.newCommunity.communityNetworkCostsStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newCommunity.communityNetworkCostsStep.subtitle")}</Typography>
                                    <SelectYesNo sx={{ minWidth: 300 }} id="invoice_network_costs" name="invoice_network_costs" label={t("label.form.invoiceNetworkCosts")} />
                                    <BillingPeriodSelect
                                        id="billing_period" name="billing_period"
                                        receipt={true}
                                        label={t("label.form.billingPeriod")}
                                        agent={selectedOwnerEntity?.agent}
                                    />
                                    <IndiePaymentTermsSelect
                                        id="sale_payment_terms" name="sale_payment_terms" label={t("label.form.paymentTerms")}
                                        agent={selectedOwnerEntity?.agent}
                                        helperText="Selecione Prazo de pagamento da energia vendida aos consumidores."
                                    />
                                    <IndieReceiptTermsSelect
                                        id="purchase_payment_terms" name="purchase_payment_terms" label={t("label.form.receiptTerms")}
                                        agent={selectedOwnerEntity?.agent}
                                        helperText="Selecione Prazo de pagamento da energia comprada aos produtores."
                                    />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={7}>
                            <StepLabel>{t("label.step.newCommunity.bannerUploadStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Box maxWidth={"md"}>
                                        <NewCommunityBannerUploadStep />
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={8}>
                            <StepLabel>{t("label.step.newCommunity.confirmationStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={4}>
                                    <NewCommunityConfirmationStep />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} disabled={submitting} />
                                        <IndieSaveButton variant="contained" id="save_button" type="submit" loading={submitting} disabled={submitting} />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                    </Stepper>
                    {/*JSON.stringify(formik.values)*/}
                </Form>
            )}
        </Formik>
    );
}

const NewCommunityBannerUploadStep = () => {
    const { t } = useTranslation();

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'> {t("label.step.newCommunity.bannerUpload.subtitle")}</Typography>
                <Divider />
                <CommunityBannerUpload id="banner_image" fieldName="banner_image" />
            </Stack>
        </Fragment>
    )
}

const NewCommunityConfirmationStep = () => {

    const { getCommunitiesTariffModels } = useContext(CommunityContext);
    const { values } = useFormikContext();
    const { t } = useTranslation();

    const [tariffModels, setTariffModels] = useState([]);
    const fetchTariffModels = () => {
        getCommunitiesTariffModels().then((response) => {
            setTariffModels(response.data);
        }).catch((error) => {
            console.error(error);
            setTariffModels([]);
        });
    }

    let statusDescription = "";
    try {
        statusDescription = CommunityStatusList.filter((s) => s.value === values.status)[0].label;
    } catch (error) {
        statusDescription = "";
    }

    let communityTypeDescription = "";
    try {
        communityTypeDescription = t(communityTypes.filter((t) => t.code === values.type)[0].name);
    } catch (error) {
        communityTypeDescription = "";
    }

    let sharingTypeDescription = "";
    try {
        sharingTypeDescription = t(communitySharingTypes.filter((t) => t.code === values.sharing_type)[0].name);
    } catch (error) {
        sharingTypeDescription = "";
    }

    let tariffModelDescription = "";
    try {
        let tariffModel = tariffModels.filter((t) => t.id === values.tariff_model)[0];
        if (tariffModel) {
            tariffModelDescription = t(`label.communities.tariffModel.title.${tariffModel.code}`);
        }
    } catch (error) {
        tariffModelDescription = "";
    }

    useEffect(() => {
        fetchTariffModels();
    }, []);

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'>
                    {t("label.step.newCommunity.confirmationStep.subtitle")}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newTariffModel.businessType")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={t(`newAgentCommunity.confirmStep.businessType.communities`)} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.menu.energyAgent")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.agent_entity.entity.name} />
                    <IndieLabelAndValue label="label.form.NIF" value={values.agent_entity.entity.fiscal_number} />
                    <IndieLabelAndValue label="label.table.header.respName" value={values.agent_entity.resp_party.name} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("newAgentCommunity.egacRegisteredDGEG")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.dgeg_name} />
                    <IndieLabelAndValue label="label.form.NIF" value={values.dgeg_fiscal_number} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunity.communityDataStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.name} />
                    <IndieLabelAndValue label="label.form.code" value={`${values.dso_id} / ${_.upperCase(values.type)}`} />
                    <IndieLabelAndValue label="label.form.status" value={t(statusDescription)} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunity.communityTypeStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.communityType" value={communityTypeDescription} />
                    <IndieLabelAndValue label="label.form.sharingType" value={sharingTypeDescription} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunity.communityNetworkCostsStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.invoiceNetworkCosts" value={values.invoice_network_costs ? t("label.form.yes") : t("label.form.no")} />
                    <IndieLabelAndValue label="label.form.billingPeriod" value={values.billing_period_object?.label} />
                    <IndieLabelAndValue label="label.form.paymentTerms" value={values.sale_payment_terms_object?.label} />
                    <IndieLabelAndValue label="label.form.receiptTerms" value={values.purchase_payment_terms_object?.label} />
                </Stack>
            </Stack>
        </Fragment>
    )
}